import React from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, Grid3X3 } from 'lucide-react';
import { Button } from '../ui/button';
import { motion } from 'framer-motion';

interface CustomCalendarToolbarProps {
  date: Date;
  view: string;
  views: string[];
  label: string;
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
  onView: (view: string) => void;
}

const CustomCalendarToolbar: React.FC<CustomCalendarToolbarProps> = ({
  date,
  view,
  views,
  label,
  onNavigate,
  onView,
}) => {
  const viewIcons = {
    month: Grid3X3,
    week: Calendar,
    day: Clock,
  };

  const viewLabels = {
    month: 'Month',
    week: 'Week', 
    day: 'Day',
  };

  return (
    <motion.div 
      className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-6 bg-gradient-to-r from-slate-50/80 to-indigo-50/80 dark:from-slate-800/80 dark:to-slate-700/80 backdrop-blur-sm border-b border-slate-200/50 dark:border-slate-600/30"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Navigation Controls */}
      <div className="flex items-center gap-2 sm:gap-3 order-2 sm:order-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('PREV')}
          className="h-9 w-9 p-0 bg-white/70 dark:bg-slate-700/70 hover:bg-white dark:hover:bg-slate-600 border-slate-200 dark:border-slate-600"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('TODAY')}
          className="px-3 sm:px-4 h-9 bg-white/70 dark:bg-slate-700/70 hover:bg-white dark:hover:bg-slate-600 border-slate-200 dark:border-slate-600 font-semibold"
        >
          <span className="hidden sm:inline">Today</span>
          <span className="sm:hidden">Now</span>
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('NEXT')}
          className="h-9 w-9 p-0 bg-white/70 dark:bg-slate-700/70 hover:bg-white dark:hover:bg-slate-600 border-slate-200 dark:border-slate-600"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar Label */}
      <div className="order-1 sm:order-2 text-center sm:text-left">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 via-indigo-600 to-purple-600 dark:from-slate-100 dark:via-indigo-300 dark:to-purple-300 bg-clip-text text-transparent">
          {label}
        </h3>
      </div>

      {/* View Controls */}
      <div className="flex items-center gap-1 sm:gap-2 order-3">
        {views.map((viewName) => {
          const Icon = viewIcons[viewName as keyof typeof viewIcons];
          const isActive = view === viewName;
          
          return (
            <Button
              key={viewName}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => onView(viewName)}
              className={`h-9 px-2 sm:px-3 ${
                isActive
                  ? 'text-white shadow-lg'
                  : 'bg-white/70 dark:bg-slate-700/70 hover:bg-white dark:hover:bg-slate-600 border-slate-200 dark:border-slate-600'
              }`}
              style={isActive ? { background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 100%)' } : {}}
            >
              <Icon className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs font-semibold">
                {viewLabels[viewName as keyof typeof viewLabels]}
              </span>
            </Button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default CustomCalendarToolbar;